import { useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";

interface DailyDataItem {
  id: string;
  dateMillis?: number | null;
  [key: string]: number | string | null | undefined;
}

interface SyncResult {
  success: boolean;
  results: {
    created: number;
    updated: number;
    errors: number;
    errorDetails: string[];
  };
}

interface DailydataListItem {
  id: string;
  dateMillis?: number | null;
  date?: { seconds?: number; _seconds?: number } | null;
  [key: string]: unknown;
}

interface DailydataListResult {
  success: boolean;
  items: DailydataListItem[];
}

interface DeleteDailydataResult {
  success: boolean;
  deleted: number;
}

type DateRangePreset = "last30days" | "thisMonth" | "lastMonth" | "last7days" | "last14days" | "custom";
type AccordionGroup = "sales" | "cost" | "prof";

type MetricField = {
  key: string;
  label: string;
  group?: AccordionGroup;
  isChild?: boolean;
};

const PAGE_SIZE = 50;
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

const SUM_AVG_FIELDS: MetricField[] = [
  { key: "total_sales", label: "売上", group: "sales" },
  { key: "google_sales", label: "Google売上", group: "sales", isChild: true },
  { key: "yahoo_sales", label: "Yahoo売上", group: "sales", isChild: true },
  { key: "af_sales", label: "AF売上", group: "sales", isChild: true },
  { key: "oth_sales", label: "その他売上", group: "sales", isChild: true },
  { key: "drape_sales", label: "ドレープ売上", group: "sales", isChild: true },
  { key: "total_cost", label: "広告費", group: "cost" },
  { key: "google_cost", label: "Goole広告費", group: "cost", isChild: true },
  { key: "yahoo_cost", label: "Yahoo広告費", group: "cost", isChild: true },
  { key: "af_cost", label: "AF広告費", group: "cost", isChild: true },
  { key: "oth_cost", label: "その他広告費", group: "cost", isChild: true },
  { key: "total_prof", label: "広告粗利", group: "prof" },
  { key: "google_prof", label: "Google粗利", group: "prof", isChild: true },
  { key: "yahoo_prof", label: "Yahoo粗利", group: "prof", isChild: true },
  { key: "af_prof", label: "AF粗利", group: "prof", isChild: true },
  { key: "oth_prof", label: "その他粗利", group: "prof", isChild: true },
  { key: "uu", label: "ユニークユーザー" },
  { key: "ss", label: "セッション" },
  { key: "cart", label: "カート" },
  { key: "checkout", label: "チェックアウト" },
  { key: "cv", label: "CV" },
];

const RATIO_FIELDS: MetricField[] = [
  { key: "cartrate_ss", label: "カート率(SS）" },
  { key: "co_cartrate", label: "CO遷移率" },
  { key: "cv_cartrate", label: "CV遷移率(カート)" },
  { key: "co_ssrate", label: "CO率(SS)" },
  { key: "cv_ssrate", label: "CV率(SS)" },
];

const ALL_METRIC_FIELDS = [...SUM_AVG_FIELDS, ...RATIO_FIELDS];

const formatDate = (value?: number | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("ja-JP");
};

const formatNumber = (value?: number | null) => {
  if (value === null || value === undefined) return "0";
  return Number(value).toLocaleString("ja-JP", { maximumFractionDigits: 2 });
};

const formatPercent = (value?: number | null) => {
  const safeValue = Number(value ?? 0);
  return `${(safeValue * 100).toFixed(2)}%`;
};


const AVERAGE_NO_DECIMAL_KEYS = new Set([
  "total_sales",
  "google_sales",
  "yahoo_sales",
  "af_sales",
  "oth_sales",
  "drape_sales",
  "total_cost",
  "google_cost",
  "yahoo_cost",
  "af_cost",
  "oth_cost",
  "total_prof",
  "google_prof",
  "yahoo_prof",
  "af_prof",
  "oth_prof",
]);

const AVERAGE_ONE_DECIMAL_KEYS = new Set(["cart", "checkout", "cv"]);

const formatAverageValue = (key: string, value?: number | null) => {
  const safeValue = Number(value ?? 0);
  if (AVERAGE_NO_DECIMAL_KEYS.has(key)) {
    return safeValue.toLocaleString("ja-JP", { maximumFractionDigits: 0 });
  }
  if (AVERAGE_ONE_DECIMAL_KEYS.has(key)) {
    return safeValue.toLocaleString("ja-JP", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  return formatNumber(safeValue);
};

const safeDivide = (numerator: number, denominator: number) => {
  if (!denominator) return 0;
  return numerator / denominator;
};

const toDateMillis = (item: DailydataListItem) => {
  if (typeof item.dateMillis === "number") {
    return item.dateMillis;
  }

  if (item.date && typeof item.date === "object") {
    const seconds = item.date.seconds ?? item.date._seconds;
    if (typeof seconds === "number") {
      return seconds * 1000;
    }
  }

  return null;
};

const pad2 = (value: number) => value.toString().padStart(2, "0");

const toJstYmd = (millis: number) => {
  const jstDate = new Date(millis + JST_OFFSET_MS);
  return `${jstDate.getUTCFullYear()}-${pad2(jstDate.getUTCMonth() + 1)}-${pad2(jstDate.getUTCDate())}`;
};

const parseYmd = (value: string) => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const getJstDayStartMillis = (value: string) => {
  const parsed = parseYmd(value);
  if (!parsed) return null;
  return Date.UTC(parsed.year, parsed.month - 1, parsed.day) - JST_OFFSET_MS;
};

const getTodayJstStartMillis = () => {
  const now = Date.now();
  const jstNow = new Date(now + JST_OFFSET_MS);
  return Date.UTC(jstNow.getUTCFullYear(), jstNow.getUTCMonth(), jstNow.getUTCDate()) - JST_OFFSET_MS;
};

const getPresetRange = (preset: Exclude<DateRangePreset, "custom">) => {
  const todayStartMillis = getTodayJstStartMillis();
  const todayJst = new Date(todayStartMillis + JST_OFFSET_MS);

  if (preset === "last30days") {
    const startMillis = todayStartMillis - ONE_DAY_MS * 29;
    return {
      startDate: toJstYmd(startMillis),
      endDate: toJstYmd(todayStartMillis),
    };
  }

  if (preset === "thisMonth") {
    const monthStartMillis = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth(), 1) - JST_OFFSET_MS;
    const monthEndExclusiveMillis = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth() + 1, 1) - JST_OFFSET_MS;
    return {
      startDate: toJstYmd(monthStartMillis),
      endDate: toJstYmd(monthEndExclusiveMillis - ONE_DAY_MS),
    };
  }

  if (preset === "lastMonth") {
    const lastMonthStartMillis = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth() - 1, 1) - JST_OFFSET_MS;
    const thisMonthStartMillis = Date.UTC(todayJst.getUTCFullYear(), todayJst.getUTCMonth(), 1) - JST_OFFSET_MS;
    return {
      startDate: toJstYmd(lastMonthStartMillis),
      endDate: toJstYmd(thisMonthStartMillis - ONE_DAY_MS),
    };
  }

  if (preset === "last7days") {
    const endMillis = todayStartMillis - ONE_DAY_MS;
    const startMillis = todayStartMillis - ONE_DAY_MS * 7;
    return {
      startDate: toJstYmd(startMillis),
      endDate: toJstYmd(endMillis),
    };
  }

  const endMillis = todayStartMillis - ONE_DAY_MS;
  const startMillis = todayStartMillis - ONE_DAY_MS * 14;
  return {
    startDate: toJstYmd(startMillis),
    endDate: toJstYmd(endMillis),
  };
};

export default function DailyDataPage() {
  const [items, setItems] = useState<DailyDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [page, setPage] = useState(1);
  const [preset, setPreset] = useState<DateRangePreset>("last30days");
  const [startDate, setStartDate] = useState(() => getPresetRange("last30days").startDate);
  const [endDate, setEndDate] = useState(() => getPresetRange("last30days").endDate);
  const [expandedGroups, setExpandedGroups] = useState<Record<AccordionGroup, boolean>>({
    sales: false,
    cost: false,
    prof: false,
  });

  const fetchItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const getDailydataListFn = httpsCallable(functions, "getDailydataList");
      const response = await getDailydataListFn();
      const payload = response.data as DailydataListResult;
      const normalizedItems: DailyDataItem[] = (payload.items || []).map((item) => {
        const metricValues = Object.fromEntries(
          ALL_METRIC_FIELDS.map(({ key }) => [key, Number(item[key] ?? 0)])
        );

        return {
          id: item.id,
          dateMillis: toDateMillis(item),
          ...metricValues,
        };
      });
      setItems(normalizedItems);
    } catch (fetchError) {
      console.error("Failed to fetch dailydata", fetchError);
      setError("デイリーデータの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const visibleFields = useMemo(
    () =>
      ALL_METRIC_FIELDS.filter((field) => {
        if (!field.group || !field.isChild) return true;
        return expandedGroups[field.group];
      }),
    [expandedGroups]
  );

  const filteredItems = useMemo(() => {
    const startMillis = getJstDayStartMillis(startDate);
    const endStartMillis = getJstDayStartMillis(endDate);

    if (startMillis === null || endStartMillis === null) {
      return items;
    }

    const endExclusiveMillis = endStartMillis + ONE_DAY_MS;

    return items.filter((item) => {
      if (typeof item.dateMillis !== "number") return false;
      return item.dateMillis >= startMillis && item.dateMillis < endExclusiveMillis;
    });
  }, [items, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedItems = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, currentPage]);

  const summaryMetrics = useMemo(() => {
    const count = filteredItems.length;
    const baseSums = Object.fromEntries(
      SUM_AVG_FIELDS.map(({ key }) => [key, filteredItems.reduce((total, item) => total + Number(item[key] ?? 0), 0)])
    ) as Record<string, number>;

    const baseAverages = Object.fromEntries(
      SUM_AVG_FIELDS.map(({ key }) => [key, count > 0 ? baseSums[key] / count : 0])
    ) as Record<string, number>;

    const ratioSums: Record<string, number> = {
      cartrate_ss: safeDivide(baseSums.cart, baseSums.ss),
      co_cartrate: safeDivide(baseSums.checkout, baseSums.cart),
      cv_cartrate: safeDivide(baseSums.cv, baseSums.cart),
      co_ssrate: safeDivide(baseSums.checkout, baseSums.ss),
      cv_ssrate: safeDivide(baseSums.cv, baseSums.ss),
    };

    const ratioAverages: Record<string, number> = {
      cartrate_ss: safeDivide(baseAverages.cart, baseAverages.ss),
      co_cartrate: safeDivide(baseAverages.checkout, baseAverages.cart),
      cv_cartrate: safeDivide(baseAverages.cv, baseAverages.cart),
      co_ssrate: safeDivide(baseAverages.checkout, baseAverages.ss),
      cv_ssrate: safeDivide(baseAverages.cv, baseAverages.ss),
    };

    return ALL_METRIC_FIELDS.map((field) => {
      if (RATIO_FIELDS.some((ratioField) => ratioField.key === field.key)) {
        return { key: field.key, label: field.label, sum: ratioSums[field.key], average: ratioAverages[field.key], isChild: field.isChild };
      }
      return { key: field.key, label: field.label, sum: baseSums[field.key] ?? 0, average: baseAverages[field.key] ?? 0, isChild: field.isChild };
    }).filter((metric) => visibleFields.some((field) => field.key === metric.key));
  }, [filteredItems, visibleFields]);

  const handlePresetChange = (value: DateRangePreset) => {
    setPreset(value);
    if (value === "custom") {
      return;
    }

    const range = getPresetRange(value);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const toggleGroup = (group: AccordionGroup) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const handleSync = async () => {
    if (!confirm("スプレッドシートから同期しますか?\n同期済みの行はスキップされます。")) {
      return;
    }

    setSyncing(true);
    setSyncResult(null);
    try {
      const syncFn = httpsCallable(functions, "syncDailydataFromSheet");
      const response = await syncFn();
      setSyncResult(response.data as SyncResult);
      await fetchItems();
    } catch (syncError: any) {
      console.error("Failed to sync dailydata", syncError);
      alert(syncError?.message || "同期に失敗しました。");
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!deleteMode || deletingAll) {
      return;
    }

    if (!confirm("dailydataの全データを削除します。\nこの操作は取り消せません。実行しますか？")) {
      return;
    }

    setDeletingAll(true);
    setError(null);
    setSyncResult(null);
    try {
      const deleteAllDailydataFn = httpsCallable(functions, "deleteAllDailydata");
      const response = await deleteAllDailydataFn();
      const payload = response.data as DeleteDailydataResult;
      alert(`全削除が完了しました。（${payload.deleted}件削除）`);
      await fetchItems();
      setPage(1);
    } catch (deleteError: any) {
      console.error("Failed to delete all dailydata", deleteError);
      alert(deleteError?.message || "全削除に失敗しました。");
    } finally {
      setDeletingAll(false);
      setDeleteMode(false);
    }
  };

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">デイリーデータ</h2>
          <p className="text-sm text-gray-600 mt-1">dailydataコレクションを日付昇順で表示（50件ずつ）</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <input
              type="checkbox"
              checked={deleteMode}
              onChange={(event) => setDeleteMode(event.target.checked)}
              disabled={deletingAll}
              className="h-4 w-4"
            />
            削除モード（一時）
          </label>

          <button
            type="button"
            onClick={handleDeleteAll}
            disabled={!deleteMode || deletingAll || syncing}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            {deletingAll ? "全削除中..." : "全削除"}
          </button>

          <button
            type="button"
            onClick={handleSync}
            disabled={syncing || deletingAll}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {syncing ? "同期中..." : "スプレッドシート同期"}
          </button>
        </div>
      </div>

      {syncResult && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex flex-wrap gap-4">
          <span>新規: {syncResult.results.created}件</span>
          <span>更新: {syncResult.results.updated}件</span>
          <span>エラー: {syncResult.results.errors}件</span>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-gray-700 flex flex-col gap-1">
            表示期間
            <select
              value={preset}
              onChange={(event) => handlePresetChange(event.target.value as DateRangePreset)}
              className="rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="last30days">直近30日間（本日含む）</option>
              <option value="thisMonth">当月</option>
              <option value="lastMonth">先月</option>
              <option value="last7days">7日間（7日前〜前日）</option>
              <option value="last14days">14日間（14日前〜前日）</option>
              <option value="custom">カスタム指定</option>
            </select>
          </label>

          <label className="text-sm text-gray-700 flex flex-col gap-1">
            開始日
            <input
              type="date"
              value={startDate}
              onChange={(event) => {
                setPreset("custom");
                setStartDate(event.target.value);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>

          <label className="text-sm text-gray-700 flex flex-col gap-1">
            終了日
            <input
              type="date"
              value={endDate}
              onChange={(event) => {
                setPreset("custom");
                setEndDate(event.target.value);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2"
            />
          </label>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-gray-700">詳細表示:</span>
          <button type="button" className="px-2 py-1 text-xs border rounded" onClick={() => toggleGroup("sales")}>売上内訳 {expandedGroups.sales ? "▲" : "▼"}</button>
          <button type="button" className="px-2 py-1 text-xs border rounded" onClick={() => toggleGroup("cost")}>広告費内訳 {expandedGroups.cost ? "▲" : "▼"}</button>
          <button type="button" className="px-2 py-1 text-xs border rounded" onClick={() => toggleGroup("prof")}>粗利内訳 {expandedGroups.prof ? "▲" : "▼"}</button>
        </div>

        <h3 className="text-base font-semibold text-gray-900 mb-3">集計（選択期間）</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">項目</th>
                <th className="text-right px-4 py-2">合計</th>
                <th className="text-right px-4 py-2">平均</th>
              </tr>
            </thead>
            <tbody>
              {summaryMetrics.map((metric) => {
                const isRatioMetric = RATIO_FIELDS.some((field) => field.key === metric.key);
                return (
                  <tr key={metric.key} className="border-t border-gray-100">
                    <td className={`px-4 py-2 ${SUM_AVG_FIELDS.find((f) => f.key === metric.key)?.isChild ? "pl-8" : ""}`}>{metric.label}</td>
                    <td className="px-4 py-2 text-right">{isRatioMetric ? formatPercent(metric.sum) : formatNumber(metric.sum)}</td>
                    <td className="px-4 py-2 text-right">{isRatioMetric ? formatPercent(metric.average) : formatAverageValue(metric.key, metric.average)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3">日付</th>
                {visibleFields.map((field) => (
                  <th key={field.key} className="text-right px-4 py-3 whitespace-nowrap">
                    {field.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={visibleFields.length + 1}>読み込み中...</td>
                </tr>
              ) : pagedItems.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-gray-500" colSpan={visibleFields.length + 1}>データがありません。</td>
                </tr>
              ) : (
                pagedItems.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(item.dateMillis)}</td>
                    {visibleFields.map((field) => {
                      const isRatioMetric = RATIO_FIELDS.some((ratioField) => ratioField.key === field.key);
                      const value = Number(item[field.key] ?? 0);
                      return (
                        <td key={field.key} className="px-4 py-3 text-right whitespace-nowrap">
                          {isRatioMetric ? formatPercent(value) : formatNumber(value)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-xs text-gray-600">
            {filteredItems.length === 0 ? "0件" : `${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, filteredItems.length)} / ${filteredItems.length}件`}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
            >
              前へ
            </button>
            <span className="text-sm text-gray-700">{currentPage} / {totalPages}</span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 disabled:opacity-50"
            >
              次へ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
